/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: remove the any in this file
// Libraries
import { Box } from '@chakra-ui/react';
import { useSize } from '@chakra-ui/react-use-size';
import { useRef } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { VictoryAxis, VictoryChart, VictoryLabel, VictoryVoronoiContainer } from 'victory';
// Components
import {
  GradientComponent,
  renderRatingRanges,
  renderVictoryAreas,
  renderVictoryLines,
  renderVictoryPointScatters,
} from './Components';
// Types
import { LineChartProps } from './LineChart.types';
// Utils
import {
  additionalAxisStyle,
  baseLabelStyles,
  cii,
  greyAxis,
  gridLayoutStyle,
  labelComponentBackground,
  labelComponentPadding,
} from '@theme/victory';

/**
 * Renders a line chart component using the Victory library.
 *
 * @component
 *
 * @param {LineChartProps} props - The line chart component props.
 * @param {boolean} props.victoryLines - Indicates whether to render Victory lines.
 * @param {ReactElement} props.tooltip - The tooltip component to be rendered.
 * @param {ChartLabels | undefined} props.labels - The chart labels to be displayed.
 * @param {TickCounts | undefined} props.tickCounts - The tick counts for the chart.
 * @param {TickFormatters | undefined} props.tickFormatters - The tick formatters for
 * the chart.
 * @param {DomainPaddingPropType} props.domainPadding - The domain padding for the
 * chart.
 * @param {RatingRanges} props.ratingRanges - The ranges in where it's expected to be the lines
 * @param {AdditionalAxis} props.additionalAxis - Additional axis to render additional axis labels
 * @param {Boolean} props.showGreyAxis - Indicates whether the chart should show a grey axis grid.
 * @param {string[]} props.tooltipBlacklist - The list of elements that avoid tooltip.
 * @returns {ReactElement} The rendered LineChart component.
 */
export const LineChart = (props: LineChartProps) => {
  const {
    victoryAreas,
    victoryLines,
    victoryPointScatters,
    tooltip,
    labels,
    tickCounts,
    tickFormatters,
    tickValues,
    domainPadding,
    ratingRanges,
    additionalAxis,
    showGreyAxis = true,
    tooltipBlacklist,
    xLimit,
    invertAxes,
    padding = 50,
    domain,
  } = props;

  const elementRef = useRef(null);
  const size = useSize(elementRef);
  const { width } = size ?? {};
  const { xLabel, yLabel } = labels ?? {};
  const { xTickCount, yTickCount } = tickCounts ?? {};
  const { xTickValues, yTickValues } = tickValues ?? {};
  const { xTickFormatter, yTickFormatter } = tickFormatters ?? {};
  const { invertXAxis = false, invertYAxis = false } = invertAxes ?? {};
  const greyAxisStyle = showGreyAxis ? greyAxis : {};
  const wheel = { smoothStep: 0.01 };

  return (
    <Box width='full' height='full' ref={elementRef} overflow='hidden'>
      <TransformWrapper wheel={wheel}>
        <TransformComponent wrapperStyle={{ width: '100%', display: 'flex' }} contentStyle={{ width: '100%' }}>
          <VictoryChart
            theme={cii}
            width={width}
            padding={padding}
            domainPadding={domainPadding}
            domain={domain}
            singleQuadrantDomainPadding={false}
            containerComponent={
              <VictoryVoronoiContainer
                labelComponent={tooltip}
                preserveAspectRatio='none'
                // We are using `labels={() => ''}` to disable the labels for the VictoryBar components.
                labels={() => ' '}
                voronoiBlacklist={tooltipBlacklist}
              />
            }
          >
            {/* X Axis */}
            <VictoryAxis
              label={xLabel}
              tickCount={xTickCount}
              tickFormat={xTickFormatter as any}
              style={greyAxisStyle}
              invertAxis={invertXAxis}
              tickValues={xTickValues}
              tickLabelComponent={
                <VictoryLabel
                  backgroundStyle={labelComponentBackground}
                  backgroundPadding={labelComponentPadding}
                  style={{
                    ...baseLabelStyles,
                  }}
                />
              }
            />
            {/* Y Axis */}
            <VictoryAxis
              dependentAxis
              tickCount={yTickCount}
              tickFormat={yTickFormatter as any}
              style={{
                ...gridLayoutStyle,
                ...greyAxisStyle,
              }}
              label={yLabel}
              invertAxis={invertYAxis}
              tickValues={yTickValues}
              axisLabelComponent={<VictoryLabel dy={-10} />}
            />
            {/* Additional Axis */}
            {additionalAxis && (
              <VictoryAxis
                style={{ ...additionalAxisStyle }}
                dependentAxis={additionalAxis.dependentAxis}
                tickValues={additionalAxis.tickValues}
                tickFormat={additionalAxis.tickFormat}
                orientation={additionalAxis.orientation}
              />
            )}
            {/* Gradients for lines */}
            <GradientComponent />
            {/* The Lines to show */}
            {victoryLines && renderVictoryLines(victoryLines)}
            {/* The rating ranges if apply */}
            {ratingRanges && renderRatingRanges(ratingRanges, xLimit)}
            {/* The areas  */}
            {victoryAreas && renderVictoryAreas(victoryAreas)}
            {/* The point scatters */}
            {victoryPointScatters && renderVictoryPointScatters(victoryPointScatters)}
          </VictoryChart>
        </TransformComponent>
      </TransformWrapper>
    </Box>
  );
};
